import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import Papa from 'papaparse';
import { EmptyState } from '@/components/page-header';
import { VatReportShell, rangeSearch } from '@/components/vat-report';
import { api } from '@/lib/api';
import { formatTrn } from '@/lib/clients';
import { formatAmount } from '@/lib/format';
import { useOrgId } from '@/lib/org';
import { downloadCsv } from '@/lib/vat';

interface VatSummary {
  orgName: string;
  trn: string | null;
  from: string;
  to: string;
  currency: string;
  rows: Array<{
    taxCodeId: string;
    code: string;
    name: string;
    rate: string;
    salesAmount: string;
    outputVat: string;
    purchaseAmount: string;
    inputVat: string;
  }>;
  totals: {
    salesAmount: string;
    outputVat: string;
    purchaseAmount: string;
    inputVat: string;
    netVat: string;
  };
  generatedAt: string;
}

export const Route = createFileRoute('/reports/vat-summary')({
  component: VatSummaryReport,
  validateSearch: rangeSearch,
});

const COLS = [
  ['salesAmount', 'Sales'],
  ['outputVat', 'Output VAT'],
  ['purchaseAmount', 'Purchases'],
  ['inputVat', 'Input VAT'],
] as const;

/** VAT summary by code (R2 VAT category): net amounts and VAT per code on each side. */
function VatSummaryReport() {
  const orgId = useOrgId();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const q = useQuery({
    queryKey: ['vat', orgId, 'report', 'summary', search.from, search.to],
    queryFn: () =>
      api<VatSummary>(`/orgs/${orgId}/vat/reports/summary?from=${search.from}&to=${search.to}`),
    enabled: Boolean(orgId),
  });
  const d = q.data;
  const exportCsv = () => {
    if (!d) return;
    const rows = [
      ['VAT code', 'Name', 'Rate %', ...COLS.map(([, l]) => l)],
      ...d.rows.map((r) => [r.code, r.name, r.rate, ...COLS.map(([k]) => r[k])]),
      ['Total', '', '', ...COLS.map(([k]) => d.totals[k])],
      ['Net VAT', '', '', '', '', '', d.totals.netVat],
    ];
    downloadCsv(`VAT summary ${search.from} to ${search.to}.csv`, Papa.unparse(rows));
  };
  return (
    <VatReportShell
      title="VAT summary"
      orgName={d?.orgName}
      trn={d ? formatTrn(d.trn) : null}
      from={search.from}
      to={search.to}
      onRun={(r) => void navigate({ search: r })}
      onExport={d ? exportCsv : undefined}
      generatedAt={d?.generatedAt}
    >
      {!orgId ? (
        <EmptyState title="Select a client" />
      ) : q.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : q.isError ? (
        <EmptyState title="Could not run the report" hint={q.error.message} />
      ) : d!.rows.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">
          No VAT transactions in this period.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-xs uppercase tracking-wide text-muted-foreground">
                <th className="py-2 text-left font-medium">VAT code</th>
                <th className="w-[80px] py-2 text-right font-medium">Rate</th>
                {COLS.map(([k, l]) => (
                  <th key={k} className="w-[150px] py-2 text-right font-medium">
                    {l}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {d!.rows.map((r) => (
                <tr key={r.taxCodeId} className="border-b border-dashed">
                  <td className="py-1.5">
                    <span className="font-medium">{r.code}</span> {r.name}
                  </td>
                  <td className="tnum py-1.5 text-right">{r.rate}%</td>
                  {COLS.map(([k]) => (
                    <td key={k} className="tnum py-1.5 text-right">
                      <Link
                        to="/reports/vat-detail"
                        search={{ from: search.from, to: search.to, code: r.code }}
                        className="text-primary hover:underline"
                      >
                        {formatAmount(r[k])}
                      </Link>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-foreground font-semibold">
                <td className="py-2" colSpan={2}>
                  TOTAL
                </td>
                {COLS.map(([k]) => (
                  <td key={k} className="tnum py-2 text-right">
                    {formatAmount(d!.totals[k])}
                  </td>
                ))}
              </tr>
              <tr className="font-semibold">
                <td className="py-2" colSpan={5}>
                  Net VAT (output − input)
                </td>
                <td className="tnum py-2 text-right" data-testid="vat-summary-net">
                  {d!.currency} {formatAmount(d!.totals.netVat)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </VatReportShell>
  );
}
